import { Injectable, inject } from '@angular/core';
import { UnitsV2 } from '@schenck/rotec-components';
import { Adapter } from 'src/utils/interfaces/adapter-service.interfaces';
import { RotorTypeFormsData } from 'src/utils/interfaces/rotor-type-forms-data.interfaces';
import { RotorTypeData } from 'src/utils/interfaces/rotor-type.interfaces';
import { CorrectionCalculationAdapter } from './correction-calculation.adapter';
import { DriveAdapter } from './drive.adapter';
import { PlaneGeometryAdapter } from './plane-geometry.adapter';
import { PlaneReferenceGeometryAdapter } from './plane-reference-geometry.adapter';
import { RotorTypeAdapter } from './rotor-type.adapter';
import { ToleranceAdapter } from './tolerances.adapter';
import { CalibrationsAndCompensationsAdapter } from './calibrations-and-compensations.adapter';
import { FilterParameterUnbalanceAdapter } from './filter-parameter-unbalance.adapter';
import { mapToRotorTypeValueV2 } from './adapter.mappers';
import { UnbalanceReadout } from 'src/utils/enums/rotor-type.enum';

@Injectable({
  providedIn: 'root',
})
export class RotorTypeDataAdapter implements Adapter<RotorTypeFormsData, RotorTypeData> {
  private readonly rotorTypeAdapter = inject(RotorTypeAdapter);
  private readonly correctionCalculationAdapter = inject(CorrectionCalculationAdapter);
  private readonly planeGeometryAdapter = inject(PlaneGeometryAdapter);
  private readonly planeReferenceGeometryAdapter = inject(PlaneReferenceGeometryAdapter);
  private readonly toleranceAdapter = inject(ToleranceAdapter);
  private readonly driveAdapter = inject(DriveAdapter);
  private readonly calibrationAndCompensations = inject(CalibrationsAndCompensationsAdapter);
  private readonly filterParameterUnbalanceAdapter = inject(FilterParameterUnbalanceAdapter);

  /**
   * Creates the adapted form data
   *
   * @param formData: RotorTypeFormsData
   * @param units: UnitsV2[]
   * @param status: string
   * @returns RotorTypeData
   */
  public adapt(formData: RotorTypeFormsData, units: UnitsV2[], status: string): RotorTypeData {
    return {
      stationIndex: 0, // station index / static number
      rotorType: this.rotorTypeAdapter.adapt(formData, null, status),
      correctionCalculation: this.correctionCalculationAdapter.adapt(formData, units),
      rotorPlaneGeometry: this.planeGeometryAdapter.adapt(formData, units),
      rotorPlaneReferenceGeometry: this.planeReferenceGeometryAdapter.adapt(formData, units),
      tolerance: this.toleranceAdapter.adapt(formData, units),
      drive: this.driveAdapter.adapt(formData, units),
      filterParameterUnbalance: this.filterParameterUnbalanceAdapter.adapt(
        formData.measurementSettingsStep,
        units
      ),
      unbalanceTransformation: {
        unbalanceDisplay: mapToRotorTypeValueV2<UnbalanceReadout>({
          value: formData.rotorTypeSettingsStep.unbalanceReadout,
          name: 'unbalanceReadout',
        }),
      },
      ...this.calibrationAndCompensations.adapt(formData),
    };
  }
}
